import { simpleMode } from '@codemirror/legacy-modes/mode/simple-mode';

import {
  booleanLiteralPattern,
  dqlUnitSuffixPattern,
  parameterNamePattern,
  sortedDqlCommands,
} from '../../../core/dql/dql-common.js';

const tokenMapping = {
  command: 'keyword',
  comment: 'comment',
  parameter: 'variableName',
  string: 'string',
  number: 'number',
  bool: 'bool',
} as const;

const startComment = [
  { regex: /\/\/.*/, token: tokenMapping.comment }, // single line comment
  { regex: /\/\*.*?/, token: tokenMapping.comment, push: 'comment' }, // start of multiline comment
];

export const dql = simpleMode({
  start: [
    ...startComment,
    {
      regex: new RegExp(
        `(\\s*)((?:${sortedDqlCommands.join('|')})\\b\\s*)`,
        'i',
      ),
      token: ['', tokenMapping.command],
      next: 'parameters',
    },
    {
      // We want to tokenize parts of a query as well, so in case of no command
      // in the start state, we go directly to the `parameters` state
      regex: /\s*\b/,
      next: 'parameters',
    },
  ],
  comment: [
    { regex: /.*?\*\//, token: tokenMapping.comment, pop: true }, // end of multiline comment
    { regex: /.*/, token: tokenMapping.comment },
  ],
  parameters: [
    ...startComment,
    { regex: parameterNamePattern, token: tokenMapping.parameter }, // parameter, eg: scanLimitGb:
    { regex: booleanLiteralPattern, token: tokenMapping.bool }, // boolean literals (true/false)
    { regex: /[A-Za-z\]]+[A-Za-z_.0-9]+/ }, // identifier, eg: runs_on or metricsJson
    { regex: /"""/, token: tokenMapping.string, push: 'tripleQuoteString' },
    { regex: /"/, token: tokenMapping.string, push: 'doubleQuoteString' },
    { regex: /`/, token: tokenMapping.string, push: 'backtickString' },
    {
      regex: new RegExp(`(\\d+(?:\\.\\d+)?${dqlUnitSuffixPattern}?)(?![.\\w])`), // Match numbers with optional unit suffix
      token: tokenMapping.number,
    },
    {
      regex: /(\[)([0-9]*)(\])/, // array index access, e.g: [0] or [200]
      token: ['', tokenMapping.number, ''], // only highlight number; do not highlight surrounding square brackets
    },
    {
      regex: /(\[)(`(?:[^`\\]|\\.)*`)(])/, // nested property access, e.g: [`app.kubernetes.io/version`]
      token: ['', tokenMapping.string, ''], // only highlight content and backticks; do not highlight surrounding square brackets
    },
    { regex: /\||\[/, next: 'start' }, // start matching a new query syntax after | or [
  ],
  tripleQuoteString: [
    { regex: /[\s\S]*"""/, token: tokenMapping.string, pop: true },
    { regex: /.*/, token: tokenMapping.string },
  ],
  doubleQuoteString: [
    { regex: /"/, token: tokenMapping.string, pop: true },
    // Note on following {0,3000000}: stop at 3 million characters per line to avoid crash.
    // Code-mirror only shows 2 million characters per line anyway.
    { regex: /(?:[^\\"]|\\(?:.|$)){0,3000000}/, token: tokenMapping.string },
  ],
  backtickString: [
    { regex: /`/, token: tokenMapping.string, pop: true },
    { regex: /(?:[^\\`]|\\(?:.|$))*/, token: tokenMapping.string },
  ],
  languageData: {
    name: 'dql',
    dontIndentStates: [],
    indentOnInput: /^\s*\}$/,
    commentTokens: { line: '//', block: { open: '/*', close: '*/' } },
    mergeTokens: false,
  },
});
